import { Directive, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay, take, takeUntil } from 'rxjs/operators';

import { isEqual } from 'lodash-es';
import { LoadingBehaviorSubject } from '../../../../../helpers/loading-behavior-subject.helper';
import { isNotNullOrUndefined } from '../../../../../helpers/typed-reactive-filters.helper';
import { IFilterData } from '../../../entities/filter-data.entity';
import { Filter } from '../../../entities/filter.class';
import { FilterType } from '../../entities/filter-type';

@Directive()
export abstract class BaseFilterDirective<T extends Filter> implements OnDestroy {
    protected readonly destroy$: Subject<void> = new Subject<void>();
    protected readonly rawFilterInput$: BehaviorSubject<T | undefined> = new BehaviorSubject<T | undefined>(undefined);
    public pureFilterStream$!: Observable<T>;

    public readonly loading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(false);

    constructor() {
        this.initPureFilterStream();
    }

    @Input() set loading(loading: boolean) {
        this.loading$.next(!!loading);
    }

    @Input() set filter(filter: T | undefined) {
        this.rawFilterInput$.next(filter);
    }

    @Output() filterChanged: EventEmitter<IFilterData> = new EventEmitter<IFilterData>();

    protected emitChange(filterdata: IFilterData): void {
        const compareFilters: (a: IFilterData | undefined, b: IFilterData | undefined) => boolean = (
            a: IFilterData | undefined,
            b: IFilterData | undefined
        ) => (!a && !b) || (a?.field === b?.field && a?.strategy === b?.strategy && isEqual(a?.values, b?.values));

        this.rawFilterInput$.pipe(take(1), takeUntil(this.destroy$)).subscribe(rawInput => {
            // SER-2463: Always emit if the filter type is date
            // Can possibly be removed
            if (
                filterdata.filterType === FilterType.Date ||
                filterdata.filterType === FilterType.TagSearch ||
                !compareFilters(rawInput?.filterData, filterdata)
            ) {
                this.filterChanged.emit(filterdata);
            }
        });
    }

    private initPureFilterStream(): void {
        this.pureFilterStream$ = this.rawFilterInput$.pipe(isNotNullOrUndefined(), shareReplay(1));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        this.rawFilterInput$.complete();
        this.loading$.complete();
    }
}
